/**
 * Photos du bâtiment et plan d'évacuation incendie — chantier 7, étape 1 (maquette 19).
 *
 * Décision d'Henrico (24/09) : dans « Vue du bâtiment », des photos et le plan
 * d'évacuation « qu'on doit garder ». Le plan est VERSIONNÉ : le remplacer archive
 * l'ancien, jamais effacé (mig 319). Photos : 12 au plus, images seulement.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Flame, X } from 'lucide-react';
import { Card, Button } from '@/components/ui';
import { api, ApiException, type DocumentsLieuDTO } from '@/lib/api';

const fr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

export function DocumentsBatiment({ lieuId }: { lieuId: string }) {
  const [docs, setDocs] = useState<DocumentsLieuDTO | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const planRef = useRef<HTMLInputElement>(null);
  const charger = useCallback(() => api.admin.lieux.documents(lieuId).then(setDocs).catch(() => setDocs(null)), [lieuId]);
  useEffect(() => { void charger(); }, [charger]);
  const envoyer = async (type: 'photo' | 'plan_evacuation', f?: File) => {
    if (!f) return;
    setErr(null);
    try { await api.admin.lieux.ajouterDocument(lieuId, type, f); await charger(); }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Envoi impossible'); }
  };

  return (
    <div className="space-y-4">
      {err && <p className="text-xs text-urgent" role="alert">{err}</p>}
      <Card variant="glass" padded>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink"><Camera className="h-4 w-4" aria-hidden="true" /> Photos</h2>
          <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" aria-label="Ajouter une photo"
            onChange={(e) => { void envoyer('photo', e.target.files?.[0]); e.target.value = ''; }} />
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => photoRef.current?.click()}>+ Ajouter</Button>
        </div>
        {!docs || docs.photos.length === 0 ? <p className="text-xs text-ink-subtle">Aucune photo du bâtiment.</p> : (
          <ul className="grid grid-cols-2 gap-2">
            {docs.photos.map((p) => (
              <li key={p.id} className="relative overflow-hidden rounded-r-md border border-border/60">
                {p.url ? <img src={p.url} alt={p.libelle ?? 'Photo du bâtiment'} className="h-28 w-full object-cover" /> : <div className="h-28 bg-surface-hi" />}
                <button type="button" aria-label="Retirer cette photo" onClick={() => void api.admin.lieux.retirerPhoto(p.id).then(charger)}
                  className="absolute right-1 top-1 rounded-r-pill bg-bg/80 p-1 text-ink hover:text-urgent"><X className="h-3.5 w-3.5" aria-hidden="true" /></button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card variant="glass" padded className="border-urgent/40">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink"><Flame className="h-4 w-4 text-urgent" aria-hidden="true" /> Plan d’évacuation incendie</h2>
        {docs?.planEvacuation ? (
          <p className="mt-1 text-xs text-ink-muted">Document de référence à conserver · version du {fr(docs.planEvacuation.createdAt)}
            {docs.plansArchives.length > 0 ? ` · ${docs.plansArchives.length} version(s) précédente(s) archivée(s)` : ''}</p>
        ) : <p className="mt-1 text-xs text-ink-subtle">Aucun plan d’évacuation déposé.</p>}
        <input ref={planRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="sr-only" aria-label="Déposer le plan d’évacuation"
          onChange={(e) => { void envoyer('plan_evacuation', e.target.files?.[0]); e.target.value = ''; }} />
        <div className="mt-3 flex gap-2">
          {docs?.planEvacuation?.url && <Button size="sm" variant="glass" onClick={() => window.open(docs.planEvacuation!.url!, '_blank', 'noopener')}>Ouvrir</Button>}
          <Button size="sm" variant="ghost" onClick={() => planRef.current?.click()}>{docs?.planEvacuation ? 'Remplacer (l’ancien est archivé)' : 'Déposer le plan'}</Button>
        </div>
      </Card>
    </div>
  );
}
